import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import "../styles/global.css";

/* ─────────────────────────────────────────────────────────────
   CATEGORY / SEVERITY TOKENS
───────────────────────────────────────────────────────────── */
const CATEGORY_COLORS = {
  "Health Camp": "#1E7A8C",
  Workshop: "#6f42c1",
  Wellness: "#fd7e14",
  Talk: "#0d6efd",
  Appointment: "#dc3545",
  default: "#1E7A8C",
};
const colorOf = (cat) => CATEGORY_COLORS[cat] || CATEGORY_COLORS.default;

const UPDATE_LEVELS = {
  urgent: { color: "#dc3545", icon: "fa-triangle-exclamation", label: "Urgent" },
  important: { color: "#fd7e14", icon: "fa-circle-exclamation", label: "Important" },
  info: { color: "#1E7A8C", icon: "fa-circle-info", label: "Update" },
};
const levelOf = (lvl) => UPDATE_LEVELS[lvl] || UPDATE_LEVELS.info;

/* ─────────────────────────────────────────────────────────────
   HERO / FALLBACK ILLUSTRATION (used when no photo is available)
───────────────────────────────────────────────────────────── */
const EventHeroIllustration = ({ color = "#1E7A8C", title = "" }) => {
  const initials = title.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const uid = `evhero${color.replace("#", "")}`;
  return (
    <svg viewBox="0 0 900 400" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
      <defs>
        <linearGradient id={uid} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.16" />
          <stop offset="100%" stopColor={color} stopOpacity="0.04" />
        </linearGradient>
      </defs>
      <rect width="900" height="400" fill={`url(#${uid})`} />
      <circle cx="800" cy="60" r="150" fill={color} fillOpacity="0.07" />
      <circle cx="100" cy="350" r="120" fill={color} fillOpacity="0.05" />
      <circle cx="450" cy="200" r="60" fill={color} fillOpacity="0.1" />
      <text x="450" y="212" textAnchor="middle" fontSize="40" fontWeight="800" fill={color} fillOpacity="0.6" fontFamily="sans-serif">
        {initials}
      </text>
      <rect x="200" y="140" width="500" height="5" rx="3" fill={color} fillOpacity="0.15" />
      <rect x="250" y="160" width="400" height="5" rx="3" fill={color} fillOpacity="0.1" />
      <rect x="300" y="180" width="300" height="5" rx="3" fill={color} fillOpacity="0.07" />
    </svg>
  );
};

/* ---- Sample fallback so the page is never blank before the API is wired up ---- */
const SAMPLE_EVENT = {
  id: "sample",
  title: "Free Community Health Camp",
  category: "Health Camp",
  date: new Date().toISOString().slice(0, 10),
  start_time: "09:00",
  end_time: "16:00",
  location: "Town Hall, Main Street",
  speaker: "Dr. A. Kumar",
  description:
    "A full-day health camp offering free check-ups, blood-sugar screening, and wellness consultations. Walk in any time between 9 AM and 4 PM.",
  content: `
    <p>Join us for a full day dedicated to community wellness. This free health camp brings together doctors, nurses, and volunteers to make basic healthcare screening accessible to everyone in the neighbourhood — no appointment needed.</p>
    <h2>What to expect</h2>
    <p>Attendees can walk through a series of stations covering general check-ups, vitals monitoring, and guidance from visiting specialists. The camp is designed to be quick and low-friction, with most visits taking under 20 minutes.</p>
    <ul>
      <li>Free blood pressure and blood-sugar screening</li>
      <li>General physician consultations</li>
      <li>Basic eye and dental check-ups</li>
      <li>One-on-one wellness and nutrition guidance</li>
    </ul>
    <h2>Who should attend</h2>
    <p>The camp is open to all age groups, though we especially encourage senior citizens and anyone who hasn't had a routine check-up in the last year to drop by. Children are welcome when accompanied by an adult.</p>
    <blockquote>Preventive care catches small issues before they become big ones — that's the whole idea behind this camp.</blockquote>
    <h2>Good to know</h2>
    <p>Seating and refreshments will be available throughout the day, and volunteers will be on hand to help you find the right station. If you take regular medication, please bring a list of what you're currently on so our doctors can give more accurate advice.</p>
  `,
  updates: [
    {
      id: "u1",
      level: "urgent",
      title: "Venue moved to Hall B",
      body: "Due to renovation work, today's camp is now in Hall B, first floor. Signage is posted at the main entrance.",
      author: "Organising Team",
      created_at: new Date().toISOString(),
    },
    {
      id: "u2",
      level: "important",
      title: "Bring a photo ID",
      body: "Please carry any government photo ID to speed up registration at the desk.",
      author: "Front Desk",
      created_at: new Date(Date.now() - 3600e3).toISOString(),
    },
    {
      id: "u3",
      level: "info",
      title: "Free refreshments available",
      body: "Water and light snacks are available near the waiting area throughout the day.",
      created_at: new Date(Date.now() - 7200e3).toISOString(),
    },
  ],
  images: "",
  videos: [
    { id: "v1", url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ", title: "Event highlights" },
  ],
};

/* ─────────────────────────────────────────────────────────────
   FORMAT HELPERS
───────────────────────────────────────────────────────────── */
const fmtDayHeading = (iso) =>
  iso
    ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "Date to be announced";

const fmt12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
};
const timeRange = (s, e) => (e ? `${fmt12(s)} - ${fmt12(e)}` : fmt12(s));

const fmtStamp = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const mediaUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u) || u.startsWith("data:")) return u;
  return `${API_BASE_URL}${u.replace(/^\/+/, "")}`;
};

const embedSrc = (url = "") => {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

const PlayCircleGlyph = () => (
  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="11" fill="rgba(0,0,0,0.55)" />
    <path d="M9.5 8.2v7.6a.7.7 0 0 0 1.06.6l6.3-3.8a.7.7 0 0 0 0-1.2l-6.3-3.8a.7.7 0 0 0-1.06.6Z" fill="#fff" />
  </svg>
);

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT  (all original data / handler logic preserved)
───────────────────────────────────────────────────────────── */
const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const canManage = Boolean(token);

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usingSample, setUsingSample] = useState(false);

  const [lightbox, setLightbox] = useState(-1);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [updateForm, setUpdateForm] = useState({ level: "info", title: "", body: "" });
  const [videoUrl, setVideoUrl] = useState("");
  const [activeMediaTab, setActiveMediaTab] = useState("past");
  const [copied, setCopied] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const showSample = () => {
    setEvent(SAMPLE_EVENT);
    setUsingSample(true);
    setLoading(false);
  };

  const loadEvent = () => {
    // No id in the URL -> show sample instead of fetching a broken URL.
    if (!id) {
      showSample();
      return;
    }
    setLoading(true);
    setUsingSample(false);
    fetch(`${API_BASE_URL}api/events/${id}`, {
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const ev = data && data.status && data.data ? data.data : null;
        if (!ev) {
          // API reachable but no event -> keep the page usable with sample content.
          showSample();
        } else {
          setEvent(ev);
          setUsingSample(false);
          setLoading(false);
        }
      })
      .catch(() => {
        // API not ready / network error -> fall back so the layout is still visible.
        showSample();
      });
  };

  useEffect(() => {
    loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  /* Backend stores images as a comma-separated filename string
     (event.images), physically saved under public/event/ — same
     convention as doctor/clinic images. Build a photos[] array with
     the shape the rest of this page already expects
     ({ id, url, caption }), so the gallery/lightbox code below needs
     no other changes. There's no video-upload support on the backend
     yet, so videos stays as whatever (if anything) the API returns. */
  const photos = useMemo(() => {
    if (!event?.images) return [];
    return event.images
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((filename, i) => ({
        id: `img-${i}`,
        url: `${API_BASE_URL}public/event/${filename}`,
      }));
  }, [event]);

  /* Backend stores videos the same way as images — a comma-separated
     filename string (event.videos), physically saved under public/event/.
     Sample/fallback data still uses an array of {url,title} objects (e.g.
     a YouTube link), so support both shapes: */
  const videos = useMemo(() => {
    if (!event) return [];
    if (typeof event.videos === "string") {
      return event.videos
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((filename, i) => ({
          id: `vid-${i}`,
          url: `${API_BASE_URL}public/event/${filename}`,
        }));
    }
    return Array.isArray(event.videos) ? event.videos : [];
  }, [event]);
  const [videoLightbox, setVideoLightbox] = useState(-1);

  const updates = useMemo(() => {
    const list = Array.isArray(event?.updates) ? [...event.updates] : [];
    return list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [event]);

  useEffect(() => {
    if (lightbox < 0) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(-1);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, photos.length]);

  useEffect(() => {
    if (videoLightbox < 0) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setVideoLightbox(-1);
      if (e.key === "ArrowRight") setVideoLightbox((i) => (i + 1) % videos.length);
      if (e.key === "ArrowLeft") setVideoLightbox((i) => (i - 1 + videos.length) % videos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [videoLightbox, videos.length]);

  const flash = (msg) => {
    setNotice(msg);
    window.setTimeout(() => setNotice(""), 4000);
  };

  const uploadFiles = (files, type) => {
    if (!files || !files.length) return;
    if (usingSample) {
      flash("Connect the API before uploading (currently showing sample data).");
      return;
    }
    setBusy(type);
    const form = new FormData();
    Array.from(files).forEach((f) => form.append("files[]", f));
    form.append("type", type);
    fetch(`${API_BASE_URL}api/events/${id}/media`, {
      method: "POST",
      headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: form,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.status) {
          flash(`${type === "photo" ? "Photos" : "Videos"} uploaded.`);
          loadEvent();
        } else {
          flash(data?.message || "Upload failed. Please try again.");
        }
      })
      .catch(() => flash("Upload failed. Check your connection and try again."))
      .finally(() => setBusy(""));
  };

  const addVideoLink = () => {
    if (!videoUrl.trim()) return;
    if (usingSample) {
      flash("Connect the API before adding videos (currently showing sample data).");
      return;
    }
    setBusy("video-link");
    fetch(`${API_BASE_URL}api/events/${id}/media`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ type: "video", url: videoUrl.trim() }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.status) {
          setVideoUrl("");
          flash("Video link added.");
          loadEvent();
        } else {
          flash(data?.message || "Could not add the video link.");
        }
      })
      .catch(() => flash("Could not add the video link."))
      .finally(() => setBusy(""));
  };

  const postUpdate = () => {
    if (!updateForm.title.trim() && !updateForm.body.trim()) {
      flash("Add a title or a message before posting.");
      return;
    }
    if (usingSample) {
      flash("Connect the API before posting updates (currently showing sample data).");
      return;
    }
    setBusy("update");
    fetch(`${API_BASE_URL}api/events/${id}/updates`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(updateForm),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.status) {
          setUpdateForm({ level: "info", title: "", body: "" });
          flash("Update posted.");
          loadEvent();
        } else {
          flash(data?.message || "Could not post the update.");
        }
      })
      .catch(() => flash("Could not post the update."))
      .finally(() => setBusy(""));
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const accent = event ? colorOf(event.category) : "#1E7A8C";
  const heroImage = photos[0]?.url ? mediaUrl(photos[0].url) : null;

  /* Loading */
  if (loading) {
    return (
      <>
        <Header />
        <div style={{ textAlign: "center", padding: "80px 32px" }}>
          <div
            style={{
              width: 44,
              height: 44,
              border: "3px solid #d6efe2",
              borderTopColor: "#1E7A8C",
              borderRadius: "50%",
              animation: "spin .8s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <p style={{ color: "#6b7280", fontSize: 14 }}>Loading event…</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <div className="ed-page">
      <style>{PAGE_STYLES}</style>
      <Header />

      {/* Breadcrumb */}
      <section className="ed-bc">
        <div className="container-fluid px-lg-5">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <a href="/">Home</a>
              </li>
              <li className="breadcrumb-item">
                <Link to="/event-day">Event Day</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {event.title && event.title.length > 55 ? `${event.title.substring(0, 55)}…` : event.title}
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {usingSample && (
        <div className="ed-banner ed-banner-warn">
          <i className="fa-solid fa-circle-info" /> Showing sample content. Connect the{" "}
          <code>api/events/{id || ":id"}</code> endpoint to load real data.
        </div>
      )}
      {notice && (
        <div className="ed-banner">
          <i className="fa-solid fa-circle-info" /> {notice}
        </div>
      )}

      <section className="ed-section">
        <div className="container-fluid px-lg-5">
          <div className="ed-layout">
            {/* ── Content ── */}
            <article className="ed-content">
              {/* Hero */}
              <div className="ed-hero">
                {heroImage ? (
                  <img src={heroImage} alt={event.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <EventHeroIllustration color={accent} title={event.title || ""} />
                )}
                {event.category && (
                  <span className="ed-hero-badge" style={{ background: accent }}>
                    {event.category}
                  </span>
                )}
              </div>

              {/* All images — horizontal scroll strip (always visible, not tab-gated) */}
              {photos.length > 0 && (
                <div className="ed-strip-wrap">
                  <div className="ed-strip">
                    {photos.map((p, i) => (
                      <button
                        type="button"
                        className="ed-strip-item"
                        key={p.id ?? i}
                        onClick={() => setLightbox(i)}
                        aria-label={`Open photo ${i + 1} of ${photos.length}`}
                      >
                        <img src={mediaUrl(p.url)} alt={p.caption || `Event photo ${i + 1}`} loading="lazy" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Header */}
              <div className="ed-art-header">
                <div className="ed-meta-row">
                  <span className="ed-mi">
                    <i className="fa-regular fa-calendar" /> {fmtDayHeading(event.date)}
                  </span>
                  {(event.start_time || event.end_time) && (
                    <span className="ed-mi">
                      <i className="fa-regular fa-clock" /> {timeRange(event.start_time, event.end_time)}
                    </span>
                  )}
                  {event.location && (
                    <span className="ed-mi">
                      <i className="fa-solid fa-location-dot" /> {event.location}
                    </span>
                  )}
                </div>

                <h1 className="ed-title">{event.title}</h1>

                {event.speaker && (
                  <div className="ed-author-bar" style={{ "--ed-accent": accent }}>
                    <div className="ed-av" style={{ background: accent }}>
                      {(event.speaker || "S").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="ed-an">{event.speaker}</div>
                      <div className="ed-ar">Speaker / Host</div>
                    </div>
                    <div className="ed-share ms-auto">
                      <span className="ed-share-lbl">Share:</span>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="ed-sb ed-fb"
                        title="Facebook"
                      >
                        <i className="fa-brands fa-facebook-f" />
                      </a>
                      <a
                        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(
                          event.title || ""
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                        className="ed-sb ed-tw"
                        title="Twitter"
                      >
                        <i className="fa-brands fa-x-twitter" />
                      </a>
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent((event.title || "") + " " + window.location.href)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="ed-sb ed-wa"
                        title="WhatsApp"
                      >
                        <i className="fa-brands fa-whatsapp" />
                      </a>
                      <button className={`ed-sb ed-cp${copied ? " ed-copied" : ""}`} onClick={handleCopy} title="Copy link">
                        <i className={`fa-solid ${copied ? "fa-check" : "fa-link"}`} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <hr className="ed-hr" />

              {/* About */}
              {(event.description || event.content) && (
                <div className="ed-body">
                  <h2 className="ed-h2">About this event</h2>
                  {event.description && <p className="ed-lede">{event.description}</p>}
                  {event.content && (
                    <div className="ed-rich-content" dangerouslySetInnerHTML={{ __html: event.content }} />
                  )}
                </div>
              )}

              {/* Important updates */}
              {/* <section className="ed-media-section">
                <div className="ed-section-head">
                  <h2 className="ed-h2">
                    <i className="fa-solid fa-bullhorn" /> Important updates
                  </h2>
                </div>

                {canManage && (
                  <div className="ed-compose">
                    <div className="ed-compose-row">
                      <select
                        className="ed-select"
                        value={updateForm.level}
                        onChange={(e) => setUpdateForm((f) => ({ ...f, level: e.target.value }))}
                      >
                        <option value="info">Update</option>
                        <option value="important">Important</option>
                        <option value="urgent">Urgent</option>
                      </select>
                      <input
                        className="ed-input"
                        type="text"
                        placeholder="Headline (e.g. Venue changed to Hall B)"
                        value={updateForm.title}
                        onChange={(e) => setUpdateForm((f) => ({ ...f, title: e.target.value }))}
                      />
                    </div>
                    <textarea
                      className="ed-textarea"
                      rows={3}
                      placeholder="Share details for attendees..."
                      value={updateForm.body}
                      onChange={(e) => setUpdateForm((f) => ({ ...f, body: e.target.value }))}
                    />
                    <button type="button" className="ed-btn" onClick={postUpdate} disabled={busy === "update"}>
                      {busy === "update" ? "Posting..." : "Post update"}
                    </button>
                  </div>
                )}

                {updates.length === 0 ? (
                  <div className="ed-empty">No updates yet. Check back closer to the event.</div>
                ) : (
                  <ol className="ed-timeline">
                    {updates.map((u) => {
                      const lv = levelOf(u.level);
                      return (
                        <li className="ed-tl-item" key={u.id} style={{ "--ed-accent": lv.color }}>
                          <span className="ed-tl-dot">
                            <i className={`fa-solid ${lv.icon}`} />
                          </span>
                          <div className="ed-tl-body">
                            <div className="ed-tl-top">
                              <span className="ed-tl-badge">{lv.label}</span>
                              {u.created_at && <time className="ed-tl-time">{fmtStamp(u.created_at)}</time>}
                            </div>
                            {u.title && <h3 className="ed-tl-title">{u.title}</h3>}
                            {u.body && <p className="ed-tl-text">{u.body}</p>}
                            {u.author && <span className="ed-tl-author">- {u.author}</span>}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </section> */}

              {/* Media: photos + videos */}
              <section className="ed-media-section">
                <div className="ed-media-tabs">
                  <button
                    type="button"
                    className={`ed-media-btn ${activeMediaTab === "past" ? "active" : ""}`}
                    onClick={() => setActiveMediaTab("past")}
                  >
                    <i className="fa-solid fa-images" /> Photos
                    {photos.length > 0 && <span className="ed-media-count">{photos.length}</span>}
                  </button>
                  <button
                    type="button"
                    className={`ed-media-btn ${activeMediaTab === "videos" ? "active" : ""}`}
                    onClick={() => setActiveMediaTab("videos")}
                  >
                    <i className="fa-solid fa-video" /> Videos
                    {videos.length > 0 && <span className="ed-media-count">{videos.length}</span>}
                  </button>
                </div>

                <div className="ed-media-panel">
                  {activeMediaTab === "past" ? (
                    <>
                      {photos.length === 0 ? (
                        <div className="ed-empty">No photos have been shared for this event yet.</div>
                      ) : (
                        <div className="ed-gallery-grid ed-gallery-scroll">
                          {photos.map((p, i) => (
                            <button
                              type="button"
                              className="ed-gallery-card"
                              key={p.id ?? i}
                              onClick={() => setLightbox(i)}
                              aria-label={p.caption || `Open photo ${i + 1}`}
                            >
                              <img src={mediaUrl(p.url)} alt={p.caption || `Event photo ${i + 1}`} loading="lazy" />
                              {p.caption && <figcaption className="ed-gallery-caption">{p.caption}</figcaption>}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {videos.length === 0 ? (
                        <div className="ed-empty">No videos have been shared for this event yet.</div>
                      ) : (
                        <div className="ed-video-grid ed-video-scroll">
                          {videos.map((v, i) => {
                            const embed = embedSrc(v.url || "");
                            return (
                              <button
                                type="button"
                                className="ed-video-card ed-video-card-btn"
                                key={v.id ?? i}
                                onClick={() => setVideoLightbox(i)}
                                aria-label={v.title || `Play video ${i + 1}`}
                              >
                                {embed ? (
                                  <div className="ed-video-thumb-wrap">
                                    <img
                                      className="ed-video-thumb"
                                      src={v.thumbnail ? mediaUrl(v.thumbnail) : `https://img.youtube.com/vi/${(v.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/) || [])[1] || ""}/hqdefault.jpg`}
                                      alt={v.title || `Event video ${i + 1}`}
                                      loading="lazy"
                                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                                    />
                                    <span className="ed-video-play"><PlayCircleGlyph /></span>
                                  </div>
                                ) : (
                                  <div className="ed-video-thumb-wrap">
                                    <video className="ed-video-thumb" src={mediaUrl(v.url)} muted preload="metadata" />
                                    <span className="ed-video-play"><PlayCircleGlyph /></span>
                                  </div>
                                )}
                                {v.title && <figcaption className="ed-video-caption">{v.title}</figcaption>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </section>

              {/* Back */}
              <div className="ed-back-row">
                <button className="ed-back-btn" onClick={() => navigate("/event-day")}>
                  <i className="fa-solid fa-arrow-left" /> Back to Events
                </button>
              </div>
            </article>

            {/* ── Sidebar ── */}
            <aside className="ed-sidebar">
              {/* Quick info card */}
              <div className="ed-sc">
                <div className="ed-sch">
                  <i className="fa-solid fa-circle-info" /> Event details
                </div>
                <div className="ed-sc-body">
                  <ul className="ed-info-list">
                    <li>
                      <i className="fa-regular fa-calendar" />
                      <span>{fmtDayHeading(event.date)}</span>
                    </li>
                    {(event.start_time || event.end_time) && (
                      <li>
                        <i className="fa-regular fa-clock" />
                        <span>{timeRange(event.start_time, event.end_time)}</span>
                      </li>
                    )}
                    {event.location && (
                      <li>
                        <i className="fa-solid fa-location-dot" />
                        <span>{event.location}</span>
                      </li>
                    )}
                    {event.speaker && (
                      <li>
                        <i className="fa-solid fa-user" />
                        <span>{event.speaker}</span>
                      </li>
                    )}
                    {event.category && (
                      <li>
                        <i className="fa-solid fa-tag" />
                        <span>{event.category}</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>

              {/* CTA */}
              <div className="ed-sc ed-cta">
                <div className="ed-cta-icon">
                  <i className="fa-solid fa-calendar-check" />
                </div>
                <div className="ed-cta-title">Want to attend?</div>
                <p className="ed-cta-text">Reserve a spot or ask us anything about this event.</p>
                <Link to="/contact" className="ed-cta-btn">
                  <i className="fa-solid fa-arrow-right" /> Book / Enquire
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {lightbox >= 0 && photos[lightbox] && (
        <div className="ed-lightbox" role="dialog" aria-modal="true" onClick={() => setLightbox(-1)}>
          <button type="button" className="ed-lb-close" aria-label="Close" onClick={() => setLightbox(-1)}>
            <i className="fa-solid fa-xmark" />
          </button>
          {photos.length > 1 && (
            <button
              type="button"
              className="ed-lb-nav ed-lb-prev"
              aria-label="Previous photo"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((i) => (i - 1 + photos.length) % photos.length);
              }}
            >
              <i className="fa-solid fa-chevron-left" />
            </button>
          )}
          <figure className="ed-lb-figure" onClick={(e) => e.stopPropagation()}>
            <img src={mediaUrl(photos[lightbox].url)} alt={photos[lightbox].caption || "Event photo"} />
            {photos[lightbox].caption && <figcaption>{photos[lightbox].caption}</figcaption>}
          </figure>
          {photos.length > 1 && (
            <button
              type="button"
              className="ed-lb-nav ed-lb-next"
              aria-label="Next photo"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((i) => (i + 1) % photos.length);
              }}
            >
              <i className="fa-solid fa-chevron-right" />
            </button>
          )}
        </div>
      )}

      {videoLightbox >= 0 && videos[videoLightbox] && (
        <div className="ed-lightbox" role="dialog" aria-modal="true" onClick={() => setVideoLightbox(-1)}>
          <button type="button" className="ed-lb-close" aria-label="Close" onClick={() => setVideoLightbox(-1)}>
            <i className="fa-solid fa-xmark" />
          </button>
          {videos.length > 1 && (
            <button
              type="button"
              className="ed-lb-nav ed-lb-prev"
              aria-label="Previous video"
              onClick={(e) => {
                e.stopPropagation();
                setVideoLightbox((i) => (i - 1 + videos.length) % videos.length);
              }}
            >
              <i className="fa-solid fa-chevron-left" />
            </button>
          )}
          <figure className="ed-lb-figure ed-lb-video-figure" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const v = videos[videoLightbox];
              const embed = embedSrc(v.url || "");
              return embed ? (
                <iframe
                  className="ed-lb-video-frame"
                  src={`${embed}?autoplay=1`}
                  title={v.title || "Event video"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video className="ed-lb-video-frame" src={mediaUrl(v.url)} controls autoPlay />
              );
            })()}
            {videos[videoLightbox].title && <figcaption>{videos[videoLightbox].title}</figcaption>}
          </figure>
          {videos.length > 1 && (
            <button
              type="button"
              className="ed-lb-nav ed-lb-next"
              aria-label="Next video"
              onClick={(e) => {
                e.stopPropagation();
                setVideoLightbox((i) => (i + 1) % videos.length);
              }}
            >
              <i className="fa-solid fa-chevron-right" />
            </button>
          )}
        </div>
      )}

      <Footer />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   STYLES (same design language as BlogDetail)
───────────────────────────────────────────────────────────── */
const PAGE_STYLES = `
  :root{
    --p:#1E7A8C;--pd:#175f6d;--pl:#e8f5f7;--pb:#b2dde4;
    --tx:#212529;--mu:#6c757d;
    --bo:#dee2e6;--bg:#f4f9fa;--wh:#ffffff;
    --r:12px;--sh:0 2px 16px rgba(30,122,140,0.08);
    --fh:'Inter',sans-serif;--fb:'Poppins',sans-serif;
  }
  .ed-page{font-family:var(--fb);background:var(--bg);min-height:100vh;}

  /* breadcrumb */
  .ed-bc{background:linear-gradient(135deg,#e8f5f7,#f0fafb);border-bottom:1px solid #b2dde4;padding:13px 0;}
  .ed-bc .breadcrumb{margin:0;}
  .ed-bc .breadcrumb-item a{color:var(--p);text-decoration:none;font-size:14px;}
  .ed-bc .breadcrumb-item.active{color:var(--mu);font-size:14px;}

  /* inline banners */
  .ed-banner{max-width:1100px;margin:14px auto 0;padding:10px 16px;border-radius:8px;background:var(--pl);border:1px solid var(--pb);color:var(--pd);font-size:13.5px;display:flex;align-items:center;gap:8px;}
  .ed-banner-warn{background:#fff7e6;border-color:#ffe1a8;color:#8a5a00;}
  .ed-banner code{background:rgba(0,0,0,0.06);padding:1px 6px;border-radius:4px;}

  /* section + layout */
  .ed-section{padding:24px 0 56px;}
  .ed-layout{display:grid;grid-template-columns:1fr 300px;gap:28px;align-items:start;}
  @media(max-width:991px){.ed-layout{grid-template-columns:1fr;}.ed-sidebar{order:-1;}}

  /* hero */
  .ed-hero{position:relative;border-radius:var(--r);overflow:hidden;aspect-ratio:16/6;margin-bottom:16px;box-shadow:var(--sh);background:#e8f5f7;}
  .ed-hero svg{display:block;width:100%;height:100%;}
  .ed-hero-badge{position:absolute;top:16px;left:16px;color:#fff;font-size:12px;font-weight:700;padding:4px 12px;border-radius:20px;font-family:var(--fh);letter-spacing:.4px;}

  /* all-images scroll strip (directly under hero, always visible) */
  .ed-strip-wrap{margin-bottom:28px;}
  .ed-strip{display:flex;gap:10px;overflow-x:auto;padding-bottom:6px;scroll-snap-type:x proximity;-webkit-overflow-scrolling:touch;}
  .ed-strip::-webkit-scrollbar{height:6px;}
  .ed-strip::-webkit-scrollbar-thumb{background:var(--pb);border-radius:10px;}
  .ed-strip::-webkit-scrollbar-track{background:transparent;}
  .ed-strip-item{flex:0 0 auto;width:120px;height:84px;border-radius:10px;overflow:hidden;border:1px solid var(--bo);padding:0;cursor:pointer;scroll-snap-align:start;background:#f8fbfc;transition:transform .15s,border-color .15s;}
  .ed-strip-item:hover{transform:translateY(-2px);border-color:var(--p);}
  .ed-strip-item img{display:block;width:100%;height:100%;object-fit:cover;}

  /* article header */
  .ed-art-header{margin-bottom:4px;}
  .ed-meta-row{display:flex;flex-wrap:wrap;gap:16px;margin-bottom:14px;}
  .ed-mi{display:flex;align-items:center;gap:5px;font-size:13px;color:var(--mu);}
  .ed-mi i{font-size:12px;color:var(--p);}
  .ed-title{font-family:var(--fh);font-size:clamp(22px,3vw,32px);font-weight:800;color:var(--tx);line-height:1.25;margin:0 0 20px;letter-spacing:-.3px;}

  /* author / speaker bar */
  .ed-author-bar{display:flex;align-items:center;gap:12px;background:var(--pl);border:1px solid var(--pb);border-radius:10px;padding:12px 16px;flex-wrap:wrap;}
  .ed-av{width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:700;color:#fff;flex-shrink:0;font-family:var(--fh);}
  .ed-an{font-size:14px;font-weight:700;color:var(--tx);font-family:var(--fh);}
  .ed-ar{font-size:12px;color:var(--mu);}

  /* share */
  .ed-share{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
  .ed-share-lbl{font-size:13px;color:var(--mu);margin-right:2px;}
  .ed-sb{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;color:#fff;border:none;cursor:pointer;transition:transform .2s,opacity .2s;text-decoration:none;}
  .ed-sb:hover{transform:translateY(-2px);opacity:.88;}
  .ed-fb{background:#1877f2;}.ed-tw{background:#000;}.ed-wa{background:#25d366;}.ed-cp{background:var(--mu);}
  .ed-copied{background:var(--p) !important;}

  /* divider */
  .ed-hr{border:none;border-top:2px solid var(--bo);margin:24px 0;}

  /* body / about */
  .ed-body{font-size:16px;line-height:1.85;color:#343a40;margin-bottom:8px;}
  .ed-body p{margin-bottom:16px;}
  .ed-body .ed-lede{font-size:16.5px;color:#495057;font-weight:500;}
  .ed-h2{font-family:var(--fh);font-size:20px;font-weight:800;color:var(--tx);margin:0 0 14px;display:flex;align-items:center;gap:8px;}
  .ed-h2 i{color:var(--p);}

  /* rich content body (dummy event content) */
  .ed-rich-content{margin-top:8px;animation:edIn .5s ease both;}
  @keyframes edIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
  .ed-rich-content h2,.ed-rich-content h3,.ed-rich-content h4{font-family:var(--fh);font-weight:700;color:var(--tx);margin:26px 0 12px;}
  .ed-rich-content h2{font-size:19px;}
  .ed-rich-content h3{font-size:16.5px;}
  .ed-rich-content h4{font-size:15px;}
  .ed-rich-content p{margin-bottom:16px;}
  .ed-rich-content a{color:var(--p);text-decoration:underline;text-underline-offset:3px;}
  .ed-rich-content img{max-width:100%;border-radius:8px;margin:16px 0;box-shadow:var(--sh);}
  .ed-rich-content ul,.ed-rich-content ol{padding-left:22px;margin-bottom:16px;}
  .ed-rich-content li{margin-bottom:6px;}
  .ed-rich-content blockquote{border-left:4px solid var(--p);background:var(--pl);padding:14px 20px;border-radius:0 8px 8px 0;margin:20px 0;font-style:italic;color:#495057;font-size:15px;}
  .ed-rich-content pre{background:#f1f3f5;border-radius:6px;padding:16px;overflow-x:auto;margin:16px 0;font-size:13.5px;}
  .ed-rich-content code{background:#f1f3f5;border-radius:4px;padding:2px 6px;font-size:13.5px;}

  /* generic card section (updates + media) */
  .ed-media-section{margin-top:24px;padding:18px;border:1px solid var(--bo);border-radius:var(--r);background:var(--wh);box-shadow:var(--sh);}
  .ed-section-head{margin-bottom:14px;}

  /* compose (update) form */
  .ed-compose{display:flex;flex-direction:column;gap:10px;margin-bottom:20px;padding:14px;background:var(--pl);border:1px solid var(--pb);border-radius:10px;}
  .ed-compose-row{display:flex;gap:10px;flex-wrap:wrap;}
  .ed-select,.ed-input,.ed-textarea{border:1px solid var(--pb);border-radius:8px;padding:9px 12px;font-size:14px;font-family:var(--fb);background:#fff;color:var(--tx);outline:none;}
  .ed-select{flex:0 0 150px;}
  .ed-input{flex:1;min-width:200px;}
  .ed-textarea{width:100%;resize:vertical;}
  .ed-select:focus,.ed-input:focus,.ed-textarea:focus{border-color:var(--p);box-shadow:0 0 0 3px rgba(30,122,140,0.12);}
  .ed-btn{align-self:flex-start;display:inline-flex;align-items:center;gap:6px;padding:9px 18px;background:var(--p);color:#fff;border:none;border-radius:50px;font-size:13.5px;font-weight:700;font-family:var(--fh);cursor:pointer;transition:background .2s,transform .15s;}
  .ed-btn:hover:not(:disabled){background:var(--pd);transform:translateY(-1px);}
  .ed-btn:disabled{opacity:.6;cursor:not-allowed;}
  .ed-btn-ghost{background:#fff;color:var(--p);border:1.5px solid var(--p);}
  .ed-btn-ghost:hover:not(:disabled){background:var(--pl);}

  /* timeline */
  .ed-timeline{list-style:none;margin:0;padding:0;position:relative;}
  .ed-tl-item{display:flex;gap:14px;position:relative;padding-bottom:22px;}
  .ed-tl-item:not(:last-child)::before{content:"";position:absolute;left:15px;top:34px;bottom:0;width:2px;background:var(--bo);}
  .ed-tl-dot{flex-shrink:0;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:var(--ed-accent);color:#fff;font-size:13px;z-index:1;}
  .ed-tl-body{flex:1;background:#f8fbfc;border:1px solid var(--bo);border-radius:10px;padding:12px 14px;}
  .ed-tl-top{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:6px;}
  .ed-tl-badge{display:inline-block;font-size:11px;font-weight:700;color:var(--ed-accent);background:rgba(0,0,0,0.04);padding:2px 9px;border-radius:20px;letter-spacing:.3px;}
  .ed-tl-time{font-size:11.5px;color:var(--mu);}
  .ed-tl-title{font-family:var(--fh);font-size:14.5px;font-weight:700;color:var(--tx);margin:0 0 4px;}
  .ed-tl-text{font-size:13.5px;color:#495057;margin:0 0 4px;line-height:1.6;}
  .ed-tl-author{font-size:12px;color:var(--mu);}

  /* media tabs */
  .ed-media-tabs{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:16px;}
  .ed-media-btn{display:inline-flex;align-items:center;gap:8px;padding:8px 14px;border:1px solid var(--pb);border-radius:999px;background:var(--pl);color:var(--p);font-size:13px;font-weight:700;cursor:pointer;transition:all .2s ease;}
  .ed-media-btn.active{background:var(--p);color:#fff;border-color:var(--p);}
  .ed-media-count{display:inline-flex;align-items:center;justify-content:center;min-width:22px;height:22px;padding:0 7px;border-radius:999px;background:rgba(255,255,255,0.22);font-size:11px;font-weight:700;}
  .ed-media-panel-head{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:16px;}
  .ed-video-link{display:flex;gap:8px;flex-wrap:wrap;flex:1;min-width:220px;}

  .ed-gallery-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:14px;}
  .ed-gallery-scroll{display:flex;grid-template-columns:none;overflow-x:auto;gap:14px;padding-bottom:6px;scroll-snap-type:x proximity;-webkit-overflow-scrolling:touch;}
  .ed-gallery-scroll::-webkit-scrollbar{height:6px;}
  .ed-gallery-scroll::-webkit-scrollbar-thumb{background:var(--pb);border-radius:10px;}
  .ed-gallery-scroll::-webkit-scrollbar-track{background:transparent;}
  .ed-gallery-scroll .ed-gallery-card{flex:0 0 auto;width:220px;scroll-snap-align:start;}
  .ed-gallery-card{overflow:hidden;border:1px solid var(--bo);border-radius:10px;background:#f8fbfc;padding:0;cursor:pointer;text-align:left;}
  .ed-gallery-card img{display:block;width:100%;aspect-ratio:4/3;object-fit:cover;}
  .ed-gallery-caption,.ed-video-caption{padding:10px 12px;font-size:13px;color:var(--mu);line-height:1.45;}
  .ed-video-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:16px;}
  .ed-video-scroll{display:flex;grid-template-columns:none;overflow-x:auto;gap:16px;padding-bottom:6px;scroll-snap-type:x proximity;-webkit-overflow-scrolling:touch;}
  .ed-video-scroll::-webkit-scrollbar{height:6px;}
  .ed-video-scroll::-webkit-scrollbar-thumb{background:var(--pb);border-radius:10px;}
  .ed-video-scroll::-webkit-scrollbar-track{background:transparent;}
  .ed-video-scroll .ed-video-card{flex:0 0 auto;width:260px;scroll-snap-align:start;}
  .ed-video-card{overflow:hidden;border:1px solid var(--bo);border-radius:10px;background:#f8fbfc;margin:0;}
  .ed-video-card-btn{padding:0;cursor:pointer;text-align:left;display:block;width:100%;border:1px solid var(--bo);}
  .ed-video-thumb-wrap{position:relative;width:100%;aspect-ratio:16/9;background:#000;overflow:hidden;}
  .ed-video-thumb{display:block;width:100%;height:100%;object-fit:cover;}
  .ed-video-play{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;transition:transform .15s ease;}
  .ed-video-card-btn:hover .ed-video-play{transform:scale(1.08);}
  .ed-video-frame{display:block;width:100%;aspect-ratio:16/9;border:0;background:#000;}
  .ed-empty{padding:14px 16px;border:1px dashed var(--pb);border-radius:10px;color:var(--mu);background:var(--pl);font-size:14px;}

  /* video lightbox frame (reuses .ed-lightbox / .ed-lb-* shell from the photo lightbox) */
  .ed-lb-video-figure{width:min(90vw,960px);}
  .ed-lb-video-frame{display:block;width:100%;aspect-ratio:16/9;border:0;border-radius:8px;background:#000;}

  /* back */
  .ed-back-row{margin-top:28px;}
  .ed-back-btn{display:inline-flex;align-items:center;gap:8px;padding:10px 20px;border:1.5px solid var(--p);border-radius:50px;color:var(--p);font-size:14px;font-weight:600;font-family:var(--fh);background:none;cursor:pointer;text-decoration:none;transition:background .2s,color .2s;}
  .ed-back-btn:hover{background:var(--p);color:#fff;}

  /* sidebar */
  .ed-sidebar{display:flex;flex-direction:column;gap:20px;}
  .ed-sc{background:var(--wh);border:1px solid var(--bo);border-radius:var(--r);overflow:hidden;box-shadow:var(--sh);}
  .ed-sch{display:flex;align-items:center;gap:8px;padding:14px 18px;background:var(--pl);border-bottom:1px solid var(--pb);font-family:var(--fh);font-size:14px;font-weight:700;color:var(--tx);}
  .ed-sch i{color:var(--p);}
  .ed-sc-body{padding:16px 18px;}
  .ed-info-list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:12px;}
  .ed-info-list li{display:flex;align-items:flex-start;gap:10px;font-size:13.5px;color:var(--tx);}
  .ed-info-list i{color:var(--p);width:16px;text-align:center;margin-top:2px;}

  /* cta */
  .ed-cta{padding:24px 20px;text-align:center;background:linear-gradient(135deg,var(--pl),#f0fafb);}
  .ed-cta-icon{width:52px;height:52px;background:var(--p);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:20px;color:#fff;margin:0 auto 14px;}
  .ed-cta-title{font-family:var(--fh);font-size:15px;font-weight:800;color:var(--tx);margin-bottom:8px;}
  .ed-cta-text{font-size:13px;color:var(--mu);margin-bottom:16px;line-height:1.5;}
  .ed-cta-btn{display:inline-flex;align-items:center;gap:6px;padding:10px 20px;background:var(--p);color:#fff;border-radius:50px;font-size:14px;font-weight:700;font-family:var(--fh);text-decoration:none;transition:background .2s,transform .15s;}
  .ed-cta-btn:hover{background:var(--pd);transform:translateY(-1px);color:#fff;}

  /* lightbox */
  .ed-lightbox{position:fixed;inset:0;background:rgba(10,20,25,0.92);z-index:1050;display:flex;align-items:center;justify-content:center;padding:24px;}
  .ed-lb-figure{max-width:90vw;max-height:85vh;margin:0;text-align:center;}
  .ed-lb-figure img{max-width:100%;max-height:80vh;border-radius:8px;display:block;margin:0 auto;}
  .ed-lb-figure figcaption{color:#e9ecef;font-size:13px;margin-top:10px;}
  .ed-lb-close{position:absolute;top:20px;right:24px;width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,0.12);color:#fff;border:none;font-size:16px;cursor:pointer;}
  .ed-lb-close:hover{background:rgba(255,255,255,0.22);}
  .ed-lb-nav{position:absolute;top:50%;transform:translateY(-50%);width:44px;height:44px;border-radius:50%;background:rgba(255,255,255,0.12);color:#fff;border:none;font-size:18px;cursor:pointer;}
  .ed-lb-nav:hover{background:rgba(255,255,255,0.22);}
  .ed-lb-prev{left:20px;}
  .ed-lb-next{right:20px;}
`;

export default EventDetail;